import { BorderLayout as SaltBorderLayout, SplitLayout as SaltSplitLayout } from "@salt-ds/core";
import {
  FlexLayout as SaltFlexLayout,
  FlowLayout as SaltFlowLayout,
  GridLayout as SaltGridLayout,
  StackLayout as SaltStackLayout,
} from "../layout/primitives";
import type {
  BorderLayoutProps,
  FlexLayoutProps,
  FlowLayoutProps,
  GridLayoutProps,
  SplitLayoutProps,
  StackLayoutProps,
} from "@salt-ds/core";
import { createElement, type ElementType } from "react";

/**
 * Salt's layout `gap` multiplier bypasses Salt's own curated mobile spacing scale.
 *
 * `StackLayout gap={N}` emits `calc(var(--salt-spacing-100) * N)`. At every density except
 * `mobile` that is arithmetically identical to `var(--salt-spacing-{N}00)`, because the theme
 * derives those steps multiplicatively:
 *
 *   .salt-density-touch|low|medium|high { --salt-spacing-300: calc(3 * var(--salt-spacing-100)) }
 *
 * `.salt-density-mobile` is the one exception. It declares a FLAT scale of literals —
 * 100:16px 150:18px 200:20px 300:24px — deliberately growing ~2px per half step so large
 * spacings do not blow up on a phone. There the multiplier and the token diverge, and the
 * divergence grows with N: gap={2} 1.6x, gap={3} 2.0x, gap={4} 2.3x. MEASURED on
 * /corporate/440/campaigns at 390x844 with a coarse pointer, two page seams rendered 48px
 * where Salt's own mobile scale asks for 24px. Blocks kept their size while the space between
 * them doubled. Evidence: docs/audit/chrome-rebuild/01-gap-facade-root-cause.md.
 *
 * The fix reads the token instead of recomputing it, so the phone gets Salt's curated value
 * and every other density is a no-op BY CONSTRUCTION rather than by hope.
 *
 * WHY THIS IS THE `gap` PROP AND NOT A STYLESHEET — Salt's `parseSpacing` returns a string
 * value verbatim (`node_modules/@salt-ds/core/dist-es/flex-layout/parseSpacing.js`), so a
 * token string is a supported, typed value for `gap` (`ResponsiveProp<number | string>`).
 * Passing the custom property through `style` instead does NOT work and looks correct in
 * review: `FlexLayout` spreads the caller's `style` FIRST and then overwrites
 * `--flexLayout-gap` with its own, so the caller's value silently vanishes. That route needs
 * `!important` in a stylesheet to win. This route needs nothing — it hands Salt a value Salt
 * already accepts.
 */

/** Salt's spacing scale runs 25..950 in quarter steps. There is no `--salt-spacing-0`. */
const MIN_TOKEN_STEP = 25;
const MAX_TOKEN_STEP = 950;

/**
 * Salt defaults `gap` to 3 on all three layout components, so a call site that omits `gap`
 * entirely is ALSO on the multiplier and ALSO renders 48px on a phone. MEASURED 2026-08-18:
 * `DashboardLayout`'s `.app-shell-page-stack` passes no `gap` and its inline declaration was
 * `calc(var(--salt-spacing-100) * 3)`. 537 opening tags in `src/` omit `gap` (StackLayout 81,
 * FlexLayout 422, GridLayout 34) — more than the 253 that write `gap={3}` explicitly. The
 * wrapper therefore has to restate Salt's default rather than pass `undefined` through.
 */
const SALT_DEFAULT_GAP = 3;

type Spacing = FlexLayoutProps<ElementType>["gap"];

/**
 * Map a Salt spacing multiplier onto the token for the same step.
 *
 * Numbers off the token grid are returned unchanged so Salt's own multiplier still applies —
 * emitting `var(--salt-spacing-717)` would resolve to nothing and silently drop the gap to
 * `normal`, which is worse than the inflation being fixed.
 */
export function curatedSpacing(value: Spacing): Spacing {
  if (value === null || value === undefined || typeof value === "string") return value;

  if (typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([breakpoint, step]) => [
        breakpoint,
        curatedSpacing(step as Spacing),
      ]),
    ) as Spacing;
  }

  // Zero is zero at every density, so there is nothing to curate — and returning the
  // number rather than a string keeps Salt's own serialization
  // (`calc(var(--salt-spacing-100) * 0)`) exactly as it was. `metric.test.tsx` asserts
  // that declaration verbatim on a `gap={0}` composition, and it is right to: a component
  // that declares zero gap is making a statement, and this fix has no business restating
  // it. There is also no `--salt-spacing-0` token to reach for.
  if (value === 0) return value;

  const step = value * 100;
  if (!Number.isInteger(step) || step % MIN_TOKEN_STEP !== 0) return value;
  if (step < MIN_TOKEN_STEP || step > MAX_TOKEN_STEP) return value;

  return `var(--salt-spacing-${step})`;
}

export const FlexLayout = (<T extends ElementType = "div">({
  gap = SALT_DEFAULT_GAP,
  ...rest
}: FlexLayoutProps<T>) =>
  createElement(SaltFlexLayout, {
    ...rest,
    gap: curatedSpacing(gap),
  } as FlexLayoutProps<T>)) as typeof SaltFlexLayout;

export const StackLayout = (<T extends ElementType = "div">({
  gap = SALT_DEFAULT_GAP,
  ...rest
}: StackLayoutProps<T>) =>
  createElement(SaltStackLayout, {
    ...rest,
    gap: curatedSpacing(gap),
  } as StackLayoutProps<T>)) as typeof SaltStackLayout;

export const GridLayout = (<T extends ElementType = "div">({
  gap = SALT_DEFAULT_GAP,
  columnGap,
  rowGap,
  ...rest
}: GridLayoutProps<T>) =>
  createElement(SaltGridLayout, {
    ...rest,
    gap: curatedSpacing(gap),
    // Left undefined when the caller omits them: Salt falls these back to `gap` internally
    // (`gridColumnGap ?? gridGap`), and restating them here would break that fallback.
    columnGap: curatedSpacing(columnGap),
    rowGap: curatedSpacing(rowGap),
  } as GridLayoutProps<T>)) as typeof SaltGridLayout;

/**
 * `FlowLayout`, `SplitLayout` and `BorderLayout` do NOT inherit the fix from the three
 * wrappers above, and that is easy to miss in review. Each one imports Salt's layout
 * primitive from inside `@salt-ds/core` itself —
 * `flow-layout/FlowLayout.js` does `import { FlexLayout } from '../flex-layout/FlexLayout.js'`
 * — so shadowing OUR `FlexLayout` export never reaches them. They have to be wrapped too.
 *
 * MEASURED 2026-08-18 on `/issuer-hub/pipeline` at 390x844 coarse-pointer, AFTER the first
 * facade landed: a `FlowLayout` rendered `calc(var(--salt-spacing-100) * 4)` = 64px where
 * `.salt-density-mobile` declares `--salt-spacing-400: 28px`. All 6 `FlowLayout` call sites
 * in `src/` pass `gap={4}`, which is the WORST divergence on the whole scale (2.3x) — the
 * one the original root-cause note predicted and the one the fix had not yet reached.
 */

/**
 * `FlowLayout` and `SplitLayout` both delegate to Salt's `FlexLayout`, whose `gap` defaults
 * to 3, so they restate that default exactly as the wrappers above do.
 */
export const FlowLayout = (<T extends ElementType = "div">({
  gap = SALT_DEFAULT_GAP,
  ...rest
}: FlowLayoutProps<T>) =>
  createElement(SaltFlowLayout, {
    ...rest,
    gap: curatedSpacing(gap),
  } as FlowLayoutProps<T>)) as typeof SaltFlowLayout;

export const SplitLayout = (<T extends ElementType = "div">({
  gap = SALT_DEFAULT_GAP,
  ...rest
}: SplitLayoutProps<T>) =>
  createElement(SaltSplitLayout, {
    ...rest,
    gap: curatedSpacing(gap),
  } as SplitLayoutProps<T>)) as typeof SaltSplitLayout;

/**
 * `BorderLayout` is the ONE exception: its `gap` defaults to 0, not 3 — it hands
 * `gap || 0` to Salt's `GridLayout`. So this wrapper must NOT restate `SALT_DEFAULT_GAP`.
 * Doing so would inject a 24px gutter into every `BorderLayout` that deliberately has
 * none — including the two app shells in `DashboardLayout.tsx`, which pass `gap={0}`
 * precisely because the shell owns its own seams. Leaving `gap` undefined here keeps
 * Salt's `|| 0` fallback intact.
 */
export const BorderLayout = (<T extends ElementType = "div">({
  gap,
  columnGap,
  rowGap,
  ...rest
}: BorderLayoutProps<T>) =>
  createElement(SaltBorderLayout, {
    ...rest,
    gap: curatedSpacing(gap),
    columnGap: curatedSpacing(columnGap),
    rowGap: curatedSpacing(rowGap),
  } as BorderLayoutProps<T>)) as typeof SaltBorderLayout;
